import React from 'react';
import { Text } from '../../atoms/Text';
import { Input } from '../../atoms/Input';
import { Button } from '../../atoms/Button';
import { Select } from '../../atoms/Select';
import { Flex } from '../../atoms/Flex';
import { Errorbox } from '../../atoms/Errorbox';
import { Noticebox } from '../../atoms/Noticebox';
import { Link } from '../../atoms/Link';
import { List } from '../../atoms/List';
import { ListItem } from '../../atoms/ListItem';
import { FormField } from '../../molecules/FormField';
import { Form } from '../../molecules/Form';

export interface SoftTransferPageProps {
  // State flags
  fromReseller?: boolean;
  transSuccess?: boolean;
  locked?: boolean;
  noSupport?: boolean;
  failure?: boolean;
  inAnotherAccount?: boolean;
  hosting?: boolean;
  delete?: boolean;
  refHasHosting?: boolean;
  badTag?: boolean;

  // Data
  domainName?: string;
  tag?: string;
  wwwDomain?: string;
  refParks?: Array<{ id: string; name: string }>;
}

export const SoftTransferPage: React.FC<SoftTransferPageProps> = ({
  fromReseller = false,
  transSuccess,
  locked = false,
  noSupport = false,
  failure,
  inAnotherAccount = false,
  hosting = false,
  delete: deleteSuccess,
  refHasHosting = false,
  badTag = false,
  domainName = '',
  tag = '',
  wwwDomain = 'www.heartinternet.uk',
  refParks = [],
}) => {
  // Handler for mediate_transfer calls
  const handleMediateTransfer = (domain: string, includeHosting: boolean) => {
    // Call the global mediate_transfer function if it exists
    const win = window as typeof window & {
      mediate_transfer?: (domain: string, includeHosting: boolean) => void;
    };
    if (typeof win.mediate_transfer === 'function') {
      win.mediate_transfer(domain, includeHosting);
    }
  };

  return (
    <div id="hi-ui-page-root" className="hi-ui-soft-transfer-page">
      {/* Page Title */}
      {fromReseller ? (
        <Text variant="heading-1">Domain References</Text>
      ) : (
        <Text variant="heading-1">Manage Domain References</Text>
      )}

      {/* Transfer Success/Error Messages */}
      {transSuccess === true && (
        <Noticebox role="alert">
          <Text variant="body">
            Transfer has been added to your <Link href="/manage/basket.cgi">basket</Link>. To
            finalise your transfer please <Link href="/manage/basket.cgi">checkout</Link>.
          </Text>
        </Noticebox>
      )}

      {transSuccess === false && !locked && (
        <Errorbox role="alert">
          <Text variant="body">
            Unable to transfer the domain into our system, if it&apos;s a{' '}
            <Text variant="body" as="span" bold>
              .uk
            </Text>{' '}
            please make sure the{' '}
            <Text variant="body" as="span" bold>
              IPSTAG
            </Text>{' '}
            is set to{' '}
            <Text variant="body" as="span" bold>
              &apos;HEARTINTERNET&apos; or &apos;EXTEND&apos;
            </Text>
            .
          </Text>
        </Errorbox>
      )}

      {locked && (
        <Errorbox role="alert">
          <Text variant="body">
            The domain appears to be locked at the registry. Please unlock the domain before
            attempting to transfer it.
          </Text>
        </Errorbox>
      )}

      {noSupport && (
        <Errorbox role="alert">
          <Text variant="body">
            Unable to transfer the domain into our system. We don&apos;t currently support that
            domain extension.
          </Text>
        </Errorbox>
      )}

      {/* Failure Messages */}
      {failure === true && (
        <>
          {inAnotherAccount ? (
            <>
              {hosting ? (
                <Errorbox role="alert">
                  <Text variant="body">
                    The domain {domainName} is in another Heart Internet Account.
                  </Text>
                  <Text variant="body">
                    Click{' '}
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleMediateTransfer(domainName, true);
                      }}
                    >
                      here
                    </Link>{' '}
                    to contact the account owner and request that permission be given to us to
                    transfer the hosting package along with the domain name. (We may charge up to
                    £50.00+VAT for this service).
                  </Text>
                  <Text variant="body">
                    Click{' '}
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleMediateTransfer(domainName, false);
                      }}
                    >
                      here
                    </Link>{' '}
                    to contact the account owner and request that only the domain name should be
                    transferred.
                  </Text>
                </Errorbox>
              ) : (
                <Errorbox role="alert">
                  <Text variant="body">
                    The domain {domainName} is in another Heart Internet Account; click{' '}
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleMediateTransfer(domainName, false);
                      }}
                    >
                      here
                    </Link>{' '}
                    to contact account owner.
                  </Text>
                </Errorbox>
              )}
            </>
          ) : (
            <Errorbox role="alert">
              <Text variant="body">Unable to add the reference.</Text>
            </Errorbox>
          )}
        </>
      )}

      {failure === false && (
        <Noticebox role="alert">
          <Text variant="body">
            Your domain reference {domainName} has been set up. To activate a web hosting account
            for this reference{' '}
            <Link href={fromReseller ? '/manage/manage-reseller-add.cgi' : '/cp/manage/webhosting'}>
              click here
            </Link>
            .
          </Text>
        </Noticebox>
      )}

      {/* Delete Success/Error Messages */}
      {deleteSuccess === true && (
        <Noticebox role="alert">
          <Text variant="body">Reference has been removed.</Text>
        </Noticebox>
      )}

      {deleteSuccess === false && (
        <Errorbox role="alert">
          <Text variant="body">
            Unable to delete the reference.{' '}
            {refHasHosting && (
              <>
                You must remove any hosting packages first. To manage your web hosting accounts
                click <Link href="/manage/manage-packages.cgi">here</Link>.
              </>
            )}
          </Text>
        </Errorbox>
      )}

      {/* Information Section */}
      <Text variant="body">
        Domain name references are used on occasions where you need to activate web hosting before
        you transfer your domain name to Heart Internet or if you don&apos;t intend to/can&apos;t
        transfer your domain name to Heart Internet.
      </Text>

      <Text variant="heading-2">Why You Might Want to do This</Text>

      <List listStyle="disc" className="cp">
        <ListItem>
          You are transferring your hosting to Heart Internet and want to get your website ready
          before transferring your domain name.
        </ListItem>
        <ListItem>
          You want to manage your domain&apos;s DNS settings through another provider.
        </ListItem>
        <ListItem>
          You want to buy hosting with Heart Internet and haven&apos;t decided on a domain name yet.
        </ListItem>
      </List>

      <Text variant="body">
        Please be aware that un-associated domain references will be deleted after 24 hours.
      </Text>

      {/* Bad Tag Error */}
      {badTag && (
        <Errorbox role="alert">
          <Text variant="body">
            This (UK) domain is not on our tag (&apos;HEARTINTERNET&apos; or &apos;EXTEND&apos;),
            it&apos;s currently on {tag}.
          </Text>
        </Errorbox>
      )}

      {/* Add Domain Reference Form */}
      <Text variant="heading-2">Add a new Domain Reference</Text>

      <Text variant="body">
        To create a domain reference simply enter the domain into the field below and click
        &quot;Add Reference&quot;, for example{' '}
        <Text variant="body" as="span" bold>
          {wwwDomain}
        </Text>{' '}
        would be{' '}
        <Text variant="body" as="span" bold>
          {wwwDomain.replace(/^www\./, '')}
        </Text>
      </Text>

      <Form action="/manage/soft_transfer.cgi" method="POST">
        {fromReseller && <input type="hidden" name="from_reseller" value="1" />}
        <Noticebox>
          <Flex direction="column" gap="md" className="hi-ui-form-container">
            <FormField label="Domain Name:" labelPosition="left">
              <Input
                type="text"
                name="domain"
                size="md"
                className="hi-ui-form-input"
                maxLength={200}
              />
            </FormField>
            <div className="hi-ui-form-actions">
              <input type="hidden" name="SU" value="ADDLINK" />
              <Button variant="primary" type="submit" className="hi-ui-form-button">
                Add Reference
              </Button>
            </div>
          </Flex>
        </Noticebox>
      </Form>

      {/* Delete Domain Reference Form */}
      {refParks && refParks.length > 0 && (
        <>
          <Text variant="heading-2">Delete a Domain Reference</Text>

          <Text variant="body">
            Simply select the reference you would like to delete, you must unassociate the domain
            from any packages before it will appear in this list.
          </Text>

          <Form action="/manage/soft_transfer.cgi" method="POST">
            {fromReseller && <input type="hidden" name="from_reseller" value="1" />}
            <Noticebox>
              <Flex direction="column" gap="md" className="hi-ui-form-container">
                <FormField label="Domain Name:" labelPosition="left">
                  <Select name="domain" size="md" className="hi-ui-form-input">
                    {refParks.map((refPark) => (
                      <option key={refPark.id} value={refPark.id}>
                        {refPark.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <div className="hi-ui-form-actions">
                  <input type="hidden" name="SU" value="DELLINK" />
                  <Button variant="warning" type="submit" className="hi-ui-form-button">
                    Delete Reference
                  </Button>
                </div>
              </Flex>
            </Noticebox>
          </Form>
        </>
      )}

      {/* Purchase or Transfer Link */}
      <Text variant="heading-2">Purchase or Transfer a Domain Reference</Text>

      <Text variant="body">
        Please{' '}
        <Link href={`/manage/soft_transfer.cgi?check=1${fromReseller ? ';from_reseller=1' : ''}`}>
          click here
        </Link>{' '}
        if you want to transfer or purchase one of your references.
      </Text>
    </div>
  );
};
